import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Download, Inbox, MoreHorizontal, Plus, SearchX, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { formatDate } from '@/lib/formatDate';
import { igHandle } from '@/lib/igHandle';
import { apiErrorMessage } from '@/lib/apiErrorMessage';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LeadStatusBadge } from '@/components/shared/LeadStatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useDraft } from '@/hooks/useDraft';
import {
  leadService,
  type Lead,
  type LeadCreateRequest,
  type LeadExportParams,
  type LeadListParams,
  type LeadStatus,
} from '@/services/leadService';

// F099/A19 — the duplicate pre-check is best-effort: a slow/failed lookup must
// NEVER block lead creation. Cap the wait so a bad connection just skips it.
const DEDUP_CHECK_TIMEOUT_MS = 1500;

const STATUS_OPTIONS: readonly LeadStatus[] = [
  'new',
  'engaged',
  'qualified',
  'follow_up',
  'won',
  'lost',
];
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

// Single height token shared by every control in the filter row. Without
// this, mixing a shadcn ``<Input>`` (h-9) with a raw ``<select>`` produced
// a visible 8px misalignment. The regression is covered by
// ``filter row controls share a height token`` in LeadsPage.test.tsx.
export const FILTER_CONTROL_CLASS = 'h-9';

export function LeadsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<Lead[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Lead | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Debounce the search input. 300ms is the §21.5 standard — tight enough
  // that typing feels live, loose enough to suppress every-keystroke fetches.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // F065 sequence guard: each fetch takes a ticket; only the latest ticket may
  // commit state, so a slow in-flight response can't overwrite a newer one.
  // See docs/07 §8 "List fetch".
  const fetchSeq = useRef(0);

  const fetchList = useCallback(async () => {
    const seq = ++fetchSeq.current;
    const params: LeadListParams = { page, page_size: PAGE_SIZE };
    if (statusFilter) params.status = statusFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    const response = await leadService.list(params);
    if (seq !== fetchSeq.current) return; // stale — a newer fetch superseded us
    setItems(response.items);
    setTotal(response.total);
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const handleCreated = async () => {
    setCreateOpen(false);
    await fetchList();
  };

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const params: LeadExportParams = {};
      if (statusFilter) params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      await leadService.exportCsv(params);
    } catch (e) {
      setExportError(apiErrorMessage(e, t, 'leads.export_error'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await leadService.delete(deleteTarget.id);
    setDeleteTarget(null);
    await fetchList();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // F065 #32: a no-result search/filter must explain itself instead of showing
  // the first-use empty state with its "create your first lead" CTA.
  const hasActiveFilters = Boolean(debouncedSearch) || Boolean(statusFilter);
  const statusLabel = statusFilter ? t(`leads.status.${statusFilter}`) : '';

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setStatusFilter('');
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={t('leads.title')}
        subtitle={t('leads.subtitle')}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              data-testid="leads-export-button"
              disabled={items === null || total === 0 || exporting}
              title={total === 0 ? t('leads.export_empty') : undefined}
              onClick={() => void handleExport()}
              className="min-h-11 md:min-h-0"
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('leads.export')}
            </Button>
            <Button
              type="button"
              data-testid="leads-new-button"
              onClick={() => setCreateOpen(true)}
              className="min-h-11 md:min-h-0"
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('leads.new_button')}
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <Input
          data-testid="leads-search-input"
          placeholder={t('leads.search_placeholder')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          className={cn(FILTER_CONTROL_CLASS, 'md:max-w-sm')}
        />
        <select
          data-testid="leads-status-filter"
          value={statusFilter}
          onChange={(event) => {
            const next = event.target.value as LeadStatus | '';
            setStatusFilter(next);
            setPage(1);
          }}
          className={cn(FILTER_CONTROL_CLASS, 'rounded-lg border bg-background px-3 text-sm')}
        >
          <option value="">{t('leads.filter_all')}</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {t(`leads.status.${status}`)}
            </option>
          ))}
        </select>
      </div>

      {exportError ? (
        // F099/A23 — the export error must be dismissible + retryable, not a
        // dead-end inline string (mirrors the TeamPage actionError affordance).
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-lg border border-destructive/50 px-3 py-1.5 text-sm text-destructive"
        >
          <span>{exportError}</span>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="leads-export-retry"
              onClick={() => void handleExport()}
            >
              {t('common.retry')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="leads-export-error-dismiss"
              aria-label={t('common.dismiss')}
              className="h-8 w-8"
              onClick={() => setExportError(null)}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      ) : null}

      {items === null ? (
        <div className="flex flex-col gap-2" data-testid="leads-skeleton">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : items.length === 0 ? (
        hasActiveFilters ? (
          <EmptyState
            icon={SearchX}
            title={
              debouncedSearch
                ? t('leads.empty_search.title', { q: debouncedSearch })
                : t('leads.empty_search.title_filter', { filter: statusLabel })
            }
            subtitle={
              debouncedSearch && statusFilter
                ? t('leads.empty_search.subtitle_with_filter', { filter: statusLabel })
                : t('leads.empty_search.subtitle')
            }
            cta={
              <Button
                type="button"
                variant="outline"
                data-testid="leads-clear-filters"
                onClick={clearFilters}
              >
                {t('leads.empty_search.cta')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={Inbox}
            title={t('leads.empty.title')}
            subtitle={t('leads.empty.subtitle')}
            cta={
              <Button
                type="button"
                data-testid="leads-empty-cta"
                onClick={() => setCreateOpen(true)}
              >
                {t('leads.empty.cta')}
              </Button>
            }
          />
        )
      ) : (
        <>
          {/* Desktop table — §21.7 pattern: hidden on mobile, replaced by cards below. */}
          <div data-testid="leads-desktop-table" className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('leads.table.name')}</TableHead>
                  <TableHead>{t('leads.table.handle')}</TableHead>
                  <TableHead>{t('leads.table.status')}</TableHead>
                  <TableHead>{t('leads.table.last_seen')}</TableHead>
                  <TableHead className="text-right">{t('leads.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((lead) => (
                  <TableRow
                    key={lead.id}
                    className="cursor-pointer"
                    data-testid={`lead-row-${lead.id}`}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                  >
                    <TableCell className="font-medium">
                      {/* cap + truncate so an unbreakable name/handle can't
                          inflate the column past the wrapper (§21.7) */}
                      <Link
                        to={`/leads/${lead.id}`}
                        className="block max-w-56 truncate hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.display_name ?? '—'}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="block max-w-56 truncate">
                        {igHandle(lead.instagram_username) ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <LeadStatusBadge
                        status={lead.status}
                        data-testid={`lead-status-badge-${lead.id}`}
                      />
                    </TableCell>
                    <TableCell>{formatDate(lead.last_seen_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('leads.delete_aria', {
                          name: lead.display_name ?? lead.instagram_username ?? '',
                        })}
                        data-testid={`lead-row-delete-${lead.id}`}
                        className={HIT_AREA_44}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(lead);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards — §21.7 pattern. */}
          <div data-testid="leads-mobile-cards" className="flex flex-col gap-3 md:hidden">
            {items.map((lead) => (
              <Link
                key={lead.id}
                to={`/leads/${lead.id}`}
                className="rounded-lg border p-4 transition-colors hover:bg-muted"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{lead.display_name ?? '—'}</span>
                    <span className="truncate text-sm text-muted-foreground">
                      {igHandle(lead.instagram_username) ?? '—'}
                    </span>
                  </div>
                  <LeadStatusBadge status={lead.status} className="shrink-0" />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDate(lead.last_seen_at)}</span>
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{t('leads.pagination.page', { page, total: totalPages })}</span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="min-h-11 md:min-h-0"
              >
                {t('leads.pagination.previous')}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="min-h-11 md:min-h-0"
              >
                {t('leads.pagination.next')}
              </Button>
            </div>
          </div>
        </>
      )}

      <CreateLeadDialog
        open={isCreateOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('leads.detail.delete_confirm_title')}
        description={t('leads.detail.delete_confirm_body')}
        confirmLabel={t('leads.detail.delete')}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

interface CreateLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void | Promise<void>;
}

interface LeadDraftValues {
  instagram: string;
  displayName: string;
  phone: string;
  email: string;
}

function CreateLeadDialog({ open, onOpenChange, onCreated }: CreateLeadDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [instagram, setInstagram] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  // Holds the payload awaiting "criar mesmo assim" confirmation (A19).
  const [dupPayload, setDupPayload] = useState<LeadCreateRequest | null>(null);
  const submitRef = useRef(false);

  // F100/A12 — recover the typed contact if the session expired mid-form.
  // Keyed by user so a shared browser never restores another account's draft.
  const draft = useDraft<LeadDraftValues>(`lead-create:${user?.id ?? 'anon'}`);

  useEffect(() => {
    if (open) {
      const restored = draft.restore();
      if (restored) {
        setInstagram(restored.instagram ?? '');
        setDisplayName(restored.displayName ?? '');
        setPhone(restored.phone ?? '');
        setEmail(restored.email ?? '');
        setDraftRestored(true);
      }
    } else {
      setInstagram('');
      setDisplayName('');
      setPhone('');
      setEmail('');
      setError(null);
      setChecking(false);
      setDupPayload(null);
      setDraftRestored(false);
    }
    // ``draft`` is stable for the dialog's lifetime; re-running on its identity
    // would loop. Only the open/close transition should restore/reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Persist (debounced) while the dialog is open; clear when emptied so an
  // all-blank form never leaves a phantom draft that re-shows the notice.
  useEffect(() => {
    if (!open) return;
    if (instagram || displayName || phone || email) {
      draft.save({ instagram, displayName, phone, email });
    } else {
      draft.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instagram, displayName, phone, email]);

  const doCreate = async (payload: LeadCreateRequest) => {
    if (submitRef.current) return;
    submitRef.current = true;
    try {
      await leadService.create(payload);
      draft.clear(); // F100 — no phantom restore after a successful create
      await onCreated();
    } catch (e) {
      setError(apiErrorMessage(e, t));
    } finally {
      submitRef.current = false;
    }
  };

  // Best-effort lookup: does a lead matching the typed contact already exist?
  // Capped by a short timeout — a slow lookup resolves to "no duplicate" so the
  // create is never blocked on the pre-check (A19 Notes).
  const hasDuplicate = async (term: string): Promise<boolean> => {
    const lookup = leadService.list({ search: term, page: 1, page_size: 1 });
    const timeout = new Promise<null>((resolve) =>
      window.setTimeout(() => resolve(null), DEDUP_CHECK_TIMEOUT_MS)
    );
    try {
      const result = await Promise.race([lookup, timeout]);
      return result !== null && result.total > 0;
    } catch {
      return false;
    }
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitRef.current || checking) return;
    if (!instagram && !displayName && !phone && !email) {
      setError(t('leads.form.contact_required'));
      return;
    }
    setError(null);
    const payload: LeadCreateRequest = {
      instagram_username: instagram || null,
      display_name: displayName || null,
      phone: phone || null,
      email: email || null,
    };

    // Pre-check by the most identifying contact the owner typed.
    const term = phone || email || instagram || displayName;
    setChecking(true);
    const duplicate = term ? await hasDuplicate(term) : false;
    setChecking(false);

    if (duplicate) {
      setDupPayload(payload); // open the non-blocking confirm
      return;
    }
    await doCreate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{t('leads.new_button')}</DialogTitle>
            <DialogDescription>{t('leads.form.contact_required')}</DialogDescription>
          </DialogHeader>
          {draftRestored ? (
            <p
              role="status"
              data-testid="lead-draft-restored"
              className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
            >
              {t('common.draft_restored')}
            </p>
          ) : null}
          {/* F069 #9 — visible labels (placeholder-only violates WCAG) */}
          <div className="flex flex-col gap-3 py-4">
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="lead-form-instagram">
                {t('leads.form.instagram_handle')}
              </label>
              <Input
                id="lead-form-instagram"
                data-testid="lead-form-instagram"
                placeholder="@exemplo"
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="lead-form-display-name">
                {t('leads.form.display_name')}
              </label>
              <Input
                id="lead-form-display-name"
                data-testid="lead-form-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="lead-form-phone">
                {t('leads.form.phone')}
              </label>
              <Input
                id="lead-form-phone"
                data-testid="lead-form-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="lead-form-email">
                {t('leads.form.email')}
              </label>
              <Input
                id="lead-form-email"
                data-testid="lead-form-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" data-testid="lead-form-submit" disabled={checking}>
              {t('leads.form.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* A19 — non-blocking duplicate warning. Confirm creates anyway; cancel
          keeps the form filled (the dialog stays mounted). */}
      <ConfirmDialog
        open={dupPayload !== null}
        onOpenChange={(o) => !o && setDupPayload(null)}
        variant="default"
        title={t('leads.duplicate_warning.title')}
        description={t('leads.duplicate_warning.body')}
        confirmLabel={t('leads.duplicate_warning.confirm')}
        cancelLabel={t('leads.duplicate_warning.cancel')}
        onConfirm={async () => {
          const payload = dupPayload;
          setDupPayload(null);
          if (payload) await doCreate(payload);
        }}
      />
    </Dialog>
  );
}
